import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Box,
  Typography,
  Stack,
  Tooltip,
  IconButton,
  Grid,
} from "@mui/material";
import {
  SecurityOutlined as ShieldIcon,
  CheckCircleOutline as VerifiedIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  OpenInNew as OpenInNewIcon,
  CodeOutlined as ContractIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { colors, borderRadiusPx } from "../../../../../theme";
import { ca as defaultContractAddress } from "../../../../Service/randomcinstance";
import {
  getExplorerAddressUrl,
  EXPLORER_LABEL,
} from "../../../../../static/Constants";

const LotterySecurityNotice = ({ contractAddress = defaultContractAddress }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!contractAddress) return;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(contractAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Contract address copied!");
    }
  };

  const explorerUrl = getExplorerAddressUrl(contractAddress);

  return (
    <Card
      className="lottery-glass-card"
      sx={{
        p: { xs: 2.5, sm: 3 },
        borderRadius: borderRadiusPx.xl,
        backgroundColor: colors.dashboard.cardBg,
        border: `1px solid ${colors.dashboard.cardBorder} !important`,
      }}
    >
      <Grid container spacing={3} alignItems="center">
        {/* Provably Fair Info */}
        <Grid item xs={12} md={7}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: borderRadiusPx.md,
                bgcolor: colors.blue[50],
                border: `1px solid ${colors.blue[200]}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: colors.primary.main,
                minWidth: 44,
              }}
            >
              <ShieldIcon sx={{ fontSize: 24 }} />
            </Box>

            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colors.slate[900] }}>
                  Decentralized & Provably Fair Ecosystem
                </Typography>
                <VerifiedIcon sx={{ fontSize: 16, color: colors.emerald[600] }} />
              </Stack>

              <Typography variant="caption" sx={{ color: colors.slate[600], mt: 0.5, display: "block" }}>
                All AutoBET lottery draws are executed via tamper-proof on-chain smart contracts with verifiable randomness. Funds and prize payouts are non-custodial and distributed automatically.
              </Typography>
            </Box>
          </Stack>
        </Grid>

        {/* Smart Contract Address */}
        <Grid item xs={12} md={5}>
          <Box
            sx={{
              p: 2,
              borderRadius: borderRadiusPx.lg,
              bgcolor: colors.slate[50],
              border: `1px solid ${colors.slate[200]}`,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
              <ContractIcon sx={{ fontSize: 16, color: colors.slate[500] }} />
              <Typography variant="caption" sx={{ color: colors.slate[600], fontWeight: 700, textTransform: "uppercase" }}>
                Smart Contract Address
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <Typography
                variant="caption"
                sx={{
                  fontFamily: "monospace",
                  color: colors.slate[800],
                  fontWeight: 600,
                  wordBreak: "break-all",
                }}
              >
                {contractAddress}
              </Typography>

              <Stack direction="row" spacing={0.5} alignItems="center">
                <Tooltip title={copied ? "Copied!" : "Copy Contract Address"}>
                  <IconButton
                    size="small"
                    onClick={handleCopy}
                    sx={{ p: 0.5, color: colors.slate[400], "&:hover": { color: colors.slate[800] } }}
                  >
                    {copied ? (
                      <CheckIcon sx={{ fontSize: 16, color: colors.success.main }} />
                    ) : (
                      <CopyIcon sx={{ fontSize: 16 }} />
                    )}
                  </IconButton>
                </Tooltip>

                {explorerUrl && (
                  <Tooltip title={EXPLORER_LABEL}>
                    <IconButton
                      size="small"
                      component="a"
                      href={explorerUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ p: 0.5, color: colors.slate[400], "&:hover": { color: colors.primary.main } }}
                    >
                      <OpenInNewIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Stack>
          </Box>
        </Grid>
      </Grid>
    </Card>
  );
};

LotterySecurityNotice.propTypes = {
  contractAddress: PropTypes.string,
};

export default LotterySecurityNotice;
